import React, { Component } from 'react';
import { computed } from 'mobx';
import { inject, observer } from 'mobx-react';
import { Flex, showSuccessToast } from 'core/components';
import { Field, FormDialog, InputGroup, RadioGroup, Select } from 'core/form';

const fields = {
  device_local: {
    label: 'Local Device',
    placeholder: 'Select a device',
    rules: 'required'
  },
  device_remote: {
    label: 'Remote Device',
    placeholder: 'Select a device',
    rules: [{ required_if: ['remote_end_type', 'monitoredDevice'] }],
    messages: {
      required_if: 'Required when Remote End Type is Monitored Device'
    }
  },
  interface_local: {
    label: 'Local Interface',
    placeholder: 'Select an interface',
    rules: [{ required_if: ['remote_end_type', 'monitoredDevice'] }],
    messages: { required_if: 'Required when Remote End Type is Monitored Device' }
  },
  interface_remote: {
    label: 'Remote Interface',
    placeholder: 'Select an interface',
    rules: [{ required_if: ['remote_end_type', 'monitoredDevice'] }],
    messages: { required_if: 'Required when Remote End Type is Monitored Device' }
  },
  remote_display_name: {
    label: 'Remote Display Name',
    rules: [{ required_if: ['remote_end_type', 'externalLink'] }],
    messages: { required_if: 'Required when Remote End Type is External Link' }
  },

  remote_end_type: {
    label: 'Remote End Type',
    defaultValue: 'monitoredDevice',
    options: [
      { value: 'monitoredDevice', label: 'Monitored Device' },
      { value: 'externalLink', label: 'External Link' }
    ]
  }
};

@inject('$metrics', '$hybridMap', '$lookups')
@observer
export default class ConnectionDialog extends Component {
  state = {};

  componentDidUpdate(prevProps) {
    const { device, model, isOpen } = this.props;
    const { form } = this.state;

    if (prevProps.isOpen !== isOpen) {
      form.setValue('device_local', device?.id);

      if (model.get('remote_display_name')) {
        form.setValue('remote_end_type', 'externalLink');
      }
    }
  }

  handleSubmit = () => {
    const { $metrics, collection } = this.props;
    const { form } = this.state;

    const options = {
      local_device_id: form.getValue('device_local'),
      local_interface_snmp_id: form.getValue('interface_local'),
      remote_device_id: form.getValue('device_remote'),
      remote_interface_snmp_id: form.getValue('interface_remote'),
      remote_display_name: form.getValue('remote_display_name')
    };

    // make sure we don't send remote_display_name if it's not needed
    // we don't necessarily want to clear out the UI value when they change the remote end type
    if (form.getValue('remote_end_type') === 'monitoredDevice') {
      delete options.remote_display_name;
    }

    collection.add(options);

    return $metrics.saveInterfaceConnection(options).then(() => {
      showSuccessToast('Connection saved successfully!');
    });
  };

  handleRemoveConnection = () => {
    const { $metrics, model, collection } = this.props;
    const { form } = this.state;

    const options = {
      local_device_id: form.getValue('device_local'),
      local_interface_snmp_id: form.getValue('interface_local'),
      remote_device_id: form.getValue('device_remote'),
      remote_interface_snmp_id: form.getValue('interface_remote'),
      remote_display_name: form.getValue('remote_display_name'),
      remove_connection: true
    };

    // remove from the local collection immediately
    collection.remove([model.id]);

    return $metrics.saveInterfaceConnection(options).then(() => {
      showSuccessToast('Connection removed successfully!');
    });
  };

  getLocalInterfaceOptions = (filter) => {
    const { $lookups } = this.props;
    const { form } = this.state;
    const { device_local } = form.getValues();
    const options = { promiseKey: 'local_interface' };

    return $lookups.interfaces(filter, options, {
      device_id: device_local
    });
  };

  getRemoteInterfaceOptions = (filter) => {
    const { $lookups } = this.props;
    const { form } = this.state;
    const { device_remote } = form.getValues();
    const options = { promiseKey: 'remote_interface' };

    return $lookups.interfaces(filter, options, {
      device_id: device_remote
    });
  };

  @computed
  get remoteDeviceOptions() {
    const { $metrics } = this.props;
    const { form } = this.state;
    const device_local = form?.getValue('device_local');
    return $metrics.availableDeviceOptions
      .map(({ value, label }) => ({ value, label }))
      .filter((opt) => opt.value !== device_local);
  }

  render() {
    const { $metrics, isOpen, onClose, model } = this.props;
    const { form } = this.state;

    const remoteEndType = form?.getValue('remote_end_type');

    return (
      <FormDialog
        isOpen={isOpen}
        fields={fields}
        options={{ name: 'Connection CRUD ' }}
        title={`${model?.isNew ? 'Add' : 'Edit'} Connection`}
        canEscapeKeyClose
        canOutsideClickClose={false}
        isCloseButtonShown
        onClose={() => onClose()}
        iconName="close"
        width={460}
        height={460}
        model={model}
        top={150}
        position="fixed"
        entityName="Connection"
        formComponentProps={{
          onLoad: (formState) => {
            this.setState({ form: formState });
          }
        }}
        formActionsProps={{
          onSubmit: this.handleSubmit,
          onCancel: onClose,
          onRemove: this.handleRemoveConnection,
          showRemove: true
        }}
      >
        <Flex flexDirection="column" gap={2}>
          <Field m={0} name="device_local" large options={$metrics.availableDeviceOptions} fill>
            <Select fill showFilter />
          </Field>
          <Field m={0} name="interface_local" large fill onQuery={this.getLocalInterfaceOptions}>
            <Select autoComplete exactMatch showFilter fill />
          </Field>

          <Field m={0} name="remote_end_type" large fill>
            <RadioGroup style={{ paddingTop: 6 }} fill />
          </Field>

          {remoteEndType === 'monitoredDevice' && (
            <Flex flexDirection="column" gap={2}>
              <Field m={0} name="device_remote" large fill options={this.remoteDeviceOptions}>
                <Select fill showFilter />
              </Field>

              <Field m={0} name="interface_remote" large fill onQuery={this.getRemoteInterfaceOptions}>
                <Select autoComplete exactMatch showFilter fill />
              </Field>
            </Flex>
          )}

          {remoteEndType === 'externalLink' && (
            <Field m={0} name="remote_display_name" large fill>
              <InputGroup fill />
            </Field>
          )}
        </Flex>
      </FormDialog>
    );
  }
}
